import React, { useEffect, useState } from 'react'
import {
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import '../page/EKGImportMain.page.css'
import '../importEvents/ImportEvents.page.css'
import { setSelectedReports, setVisitedReports } from '../store/ekgImport.actions'
import { getEKGImportSelector } from '../selectors'
import { IToggleState } from '../store/ekgImport.types'
import { chevronBack, chevronForward } from 'ionicons/icons'
import ReportItem from '../reportItem/ReportItem.component'
import Logger from '../../../services/logger.service'

interface IImportReportsProps {
  onPrevious: () => void
  onNext: () => void
  onCancel: () => void
}

interface IImportReportsState {
  toggleState: IToggleState
  importAllStatus: boolean
}

const initialState: IImportReportsState = {
  toggleState: null,
  importAllStatus: false,
}

const ImportReports = (props: IImportReportsProps) => {
  const { onPrevious, onNext, onCancel } = props

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { reportsList, selectedReports, visitedReports, isLoading } = useSelector(getEKGImportSelector)

  const [state, setState] = useState<IImportReportsState>(initialState)
  const { toggleState, importAllStatus } = state

  const initialToggleAllOn = () => {
    const newToggleState = {
      ...Object.fromEntries(
        reportsList.map((report) => {
          const isChecked = true

          return [report.id, isChecked]
        })
      ),
    }

    setState((prevState) => ({
      ...prevState,
      toggleState: newToggleState,
    }))
  }

  const onMount = () => {
    Logger.info('ECGImport: The user is on the report selection screen')
    if (!visitedReports) {
      initialToggleAllOn()
      dispatch(setVisitedReports(true))
    }
  }

  useEffect(() => {
    onMount()
  }, [])

  const fillToggleState = () => {
    const newToggleState = {
      ...Object.fromEntries(
        reportsList.map((report) => {
          const isChecked = selectedReports.some((selectedReport) => report.id === selectedReport.id)

          return [report.id, isChecked]
        })
      ),
    }

    setState((prevState) => ({
      ...prevState,
      toggleState: newToggleState,
      useImportByDefault: false,
    }))
  }

  useEffect(() => {
    if (visitedReports && reportsList.length) fillToggleState()
  }, [reportsList])

  const checkForAllImported = () => {
    const allImported = Object.values(toggleState as Record<string, boolean>).every((toggleStatus) => toggleStatus)
    const noneImported = Object.values(toggleState as Record<string, boolean>).some((toggleStatus) => !toggleStatus)

    if (allImported) {
      setState((prevState) => ({
        ...prevState,
        importAllStatus: true,
      }))
    } else if (noneImported) {
      setState((prevState) => ({
        ...prevState,
        importAllStatus: false,
      }))
    }
  }

  useEffect(() => {
    if (toggleState === null || !Object.keys(toggleState).length) return

    checkForAllImported()
  }, [toggleState])

  const onToggleReport = (id: string) => {
    if (!toggleState) return

    const isChecked = !toggleState[id]
    const newToggleState = {
      ...toggleState,
      [id]: isChecked,
    }

    setState((prevState) => ({
      ...prevState,
      toggleState: newToggleState,
    }))
  }

  const onToggleImportAll = () => {
    if (!toggleState) return

    const newImportAllStatus = !importAllStatus
    const toggleIds = Object.keys(toggleState)

    const newToggleState = {
      ...Object.fromEntries(toggleIds.map((k) => [k, newImportAllStatus])),
    }

    setState((prevState) => ({
      ...prevState,
      toggleState: newToggleState,
      importAllStatus: newImportAllStatus,
    }))
  }

  const handlePrevious = () => {
    if (toggleState) {
      const checkedReports = reportsList.filter((report) => {
        return toggleState[report.id]
      })

      dispatch(setSelectedReports(checkedReports))
    }

    onPrevious()
  }

  const handleNext = () => {
    if (toggleState) {
      const checkedReports = reportsList.filter((report) => {
        return toggleState[report.id]
      })

      dispatch(setSelectedReports(checkedReports))
    }

    onNext()
  }

  const renderFooter = () => {
    const nextMessage =
      toggleState && Object.values(toggleState).some((isChecked) => isChecked)
        ? 'common.buttons.Next'
        : 'common.buttons.Skip'

    return (
      <IonFooter className='ekg-footer'>
        <IonButton className='ekg-footer-button' onClick={onCancel}>
          {t('common.buttons.Cancel')}
        </IonButton>
        <div className='ekg-footer-button-group'>
          <IonButton className='ekg-footer-button' onClick={handlePrevious}>
            <IonIcon className='ekg-footer-button-chevron' icon={chevronBack} color={'#6fa5d6'} />
            {t('common.buttons.Back')}
          </IonButton>
          <IonButton className='ekg-footer-button-alt' onClick={handleNext}>
            {t(nextMessage)}
            <IonIcon className='ekg-footer-button-chevron' icon={chevronForward} color={'#6fa5d6'} />
          </IonButton>
        </div>
      </IonFooter>
    )
  }

  const renderTableHeader = () => {
    return (
      <>
        <IonItem className='event-header-item'>
          <div className='event-item-main'>
            <IonToggle slot='start' checked={importAllStatus} onClick={onToggleImportAll} />
            <IonGrid className='event-item-grid'>
              <IonRow>
                <IonCol className='event-description-report'>
                  <IonLabel>
                    <h3>{t('ekg.name')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-size'>
                  <IonLabel>
                    <h3>{t('ekg.size')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-more'></IonCol>
              </IonRow>
            </IonGrid>
          </div>
        </IonItem>
        <div className='event-header-hr' />
      </>
    )
  }

  return (
    <>
      <IonContent fullscreen>
        <IonList lines='full'>
          {renderTableHeader()}
          {!!reportsList.length && !isLoading && (
            <>
              {reportsList.map((report, i) => {
                const isChecked = toggleState ? toggleState[report.id] : false

                return (
                  <ReportItem key={i} report={report} showToggle isChecked={isChecked} handleToggle={onToggleReport} />
                )
              })}
            </>
          )}
        </IonList>
      </IonContent>
      {renderFooter()}
    </>
  )
}

export default ImportReports
